import { Menu, UnstyledButton, Text, ScrollArea } from "@mantine/core"
import { CellId } from "../duty-roster-spreadsheet"
import { renderContent } from "./renderCellContent"
import clsx from "clsx"
import css from "./styles.module.css"
import { ts_Schicht } from "../../../../../@client/Theasoft_DataClasses_Perso"
import { useQueryGet } from "../../../../../typed-client/hooks/use-query-get"
import { useTranslation } from "react-i18next"
import {
  getContractId,
  getDepartmentId,
  getWorkareaId,
  useGridStore,
} from "../state"
import { useShallow } from "zustand/react/shallow"
import { useSuspenseQueryGet } from "../../../../../typed-client/hooks/use-suspense-query-get"
import { SuspenseWithFallback } from "../../../../../-components/suspense"
import {
  Cell,
  OrchestraActivityDto,
  DutySchedulingRowHeader as RowHeader,
} from "@/orval/gen/model"
import { isArtisticModule } from "@/utils/module"
import { useRequestablePresenceShiftTypes } from "../../../../../blocks/shift-types/useRequestablePresenceShiftTypes"
import { useRequestableAbsenceShiftTypes } from "../../../../../blocks/shift-types/useRequestableAbsenceShiftTypes"

interface Props {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  containerProps: any
  cell: Cell | undefined
  cellId: CellId
  header: RowHeader
}

function MenuDropdown({
  cellId,
  activity,
}: {
  cellId: CellId
  activity: OrchestraActivityDto | null
}) {
  const { t } = useTranslation("app_server")
  const { cell, setModal } = useGridStore(
    useShallow((s) => ({
      cell: s.items?.cellsById?.[cellId] ?? null,
      isSelected: s.selected === cellId,
      setSelected: s.setSelected,
      setModal: s.setModalState,
    }))
  )
  const departmentId = getDepartmentId(cellId)
  const { data: module } = useSuspenseQueryGet(
    "/api/Department/GetModule",
    {
      departmentId,
    },
    { gcTime: 4 * 60_000 }
  )
  const activityId = activity === null ? null : activity.activityId
  const { data: castableRoles } = useQueryGet(
    "/api/Orchestra/GetCastableRoles",
    {
      contractId: getContractId(cellId),
      workareaId: getWorkareaId(cellId),
      activityId: activityId!,
      filterCastedRoles: true,
    },
    { enabled: activityId !== null }
  )

  const { data: presenceShiftTypes } = useRequestablePresenceShiftTypes({
    departmentId,
  })
  const { data: absenceShiftTypes } = useRequestableAbsenceShiftTypes({
    departmentId,
  })

  const items = [
    ...(cell?.casts?.length
      ? [
          <React.Fragment key="casts">
            {cell?.casts?.map((x, i) => (
              <Menu.Item
                key={i}
                onClick={() => {
                  setModal({ view: "edit-cast", cellId, castId: x.castId! })
                }}
              >
                <Text>{x.roleName}</Text>
              </Menu.Item>
            ))}
            <Menu.Divider />
          </React.Fragment>,
        ]
      : []),

    ...(castableRoles?.length
      ? [
          <React.Fragment key="roles">
            <Menu.Label>{t("CastVerb")}</Menu.Label>
            {castableRoles?.map((x, i) => (
              <Menu.Item
                key={i}
                onClick={() => {
                  setModal({ view: "add-cast", cellId, roleId: x.id })
                }}
              >
                <Text>{x.name}</Text>
              </Menu.Item>
            ))}
            <Menu.Divider />
          </React.Fragment>,
        ]
      : []),

    ...(cell?.absences?.length
      ? [
          <React.Fragment key="person-status-absences">
            {cell?.absences?.map((x, i) => (
              <Menu.Item
                //TODO: currently only person status absences are editable
                disabled={!isArtisticModule(module!)}
                key={i}
                onClick={() => {
                  setModal({
                    view: "edit-personstatus-absence",
                    cellId,
                    personStatus: {
                      ...x,
                    },
                  })
                }}
              >
                <Text>{(x as any).label || x.statusDisplayName}</Text>
              </Menu.Item>
            ))}
            <Menu.Divider />
          </React.Fragment>,
        ]
      : []),

    ...(cell?.dutyRosters?.length
      ? [
          <React.Fragment key="duty-rosters">
            {cell?.dutyRosters?.flatMap((v) =>
              v.shifts!.map((x, i) => (
                <Menu.Item
                  key={i}
                  onClick={() => {
                    if (x.isAbsenceShift) {
                      setModal({
                        cellId,
                        view: "edit-absence-shift",
                        dutyRosterId: v.dutyRosterId!,
                        shiftId: x.shiftId as ts_Schicht,
                      })
                    } else {
                      setModal({
                        cellId,
                        view: "edit-shift",
                        dutyRosterId: v.dutyRosterId!,
                        shiftId: x.shiftId as ts_Schicht,
                      })
                    }
                  }}
                >
                  <Text>
                    {x.start && x.end
                      ? `${x.start.substring(0, 5)} ${x.end.substring(0, 5)} `
                      : null}
                    {x.label}
                  </Text>
                </Menu.Item>
              ))
            )}
            <Menu.Divider />
          </React.Fragment>,
        ]
      : []),

    ...(absenceShiftTypes.length
      ? [
          <React.Fragment key="absence">
            {absenceShiftTypes.map((v, i) => (
              <Menu.Item
                key={i}
                onClick={() => {
                  if (module === "theaPerso") {
                    setModal({ cellId, view: "add-absence", shiftType: v })
                  } else {
                    setModal({
                      cellId,
                      view: "add-personstatus-absence",
                      shiftType: v,
                    })
                  }
                }}
              >
                <Text>{t("EnterX", { x: v.name })}</Text>
              </Menu.Item>
            ))}
            <Menu.Divider />
          </React.Fragment>,
        ]
      : []),

    ...(presenceShiftTypes.length
      ? [
          <React.Fragment key="presence">
            {presenceShiftTypes.map((v, i) => (
              <Menu.Item
                key={i}
                onClick={() => {
                  if (module === "theaPerso") {
                    if (
                      v.startTimeDefault1 === null &&
                      v.startTimeDefault2 === null &&
                      v.startTimeDefault3 === null
                    ) {
                      setModal({ cellId, view: "add-shift", shiftType: v })
                    } else {
                      // TODO: mutate directly without showing modal
                      setModal({ cellId, view: "add-shift", shiftType: v })
                    }
                  } else {
                    setModal({
                      cellId,
                      view: "add-personstatus-shift",
                      shiftTypeId: v.shiftTypeId,
                    })
                  }
                }}
              >
                <Text>{v.name}</Text>
              </Menu.Item>
            ))}
          </React.Fragment>,
        ]
      : []),
  ]

  return items.length === 0 ? (
    <Menu.Label>{t("NoOptionsFound")}</Menu.Label>
  ) : (
    <ScrollArea.Autosize mah={200} w={230}>
      {items}
    </ScrollArea.Autosize>
  )
}

export const MenuCellRenderer = ({
  containerProps: props,
  cell,
  cellId,
  header,
}: Props) => {
  const { setSelected, isOpened, isActive, activity } = useGridStore(
    useShallow((s) => ({
      isOpened: s.selected === cellId,
      isActive: s.selected === cellId || s.modalState?.cellId === cellId,
      setSelected: s.setSelected,
      activity: s.getActivity(cellId),
    }))
  )

  const { editable } = header

  return (
    <Menu
      disabled={!editable}
      opened={isOpened}
      onClose={() => setSelected(null)}
      onOpen={() => {
        setSelected(cellId)
      }}
    >
      <Menu.Target>
        <UnstyledButton
          // data-cellid={cellId}
          // onMouseOver={() => ctx.setMouseOver(cellId)}
          // onMouseLeave={() => ctx.setMouseOver(null)}
          {...props}
          className={clsx(
            css["cell"],
            !editable && css["not-editable"],
            // !Boolean(activity) && css["not-editable"],
            (isOpened || isActive) && css["selected-cell"]
          )}
          style={{
            ...props?.style,
            cursor: editable ? "pointer" : "default",
            display: "flex",
          }}
        >
          {cell ? renderContent(cell) : null}
        </UnstyledButton>
      </Menu.Target>
      <Menu.Dropdown>
        <SuspenseWithFallback>
          <MenuDropdown cellId={cellId} activity={activity} />
        </SuspenseWithFallback>
      </Menu.Dropdown>
    </Menu>
  )
}
